import { Injectable, computed, inject, signal } from '@angular/core';
import { hasEntitlement, findPermissionNode } from '../auth/permission-access';
import { NavigationItem, NavigationItemStatus } from '../context/current-context.model';
import { CurrentContextService } from '../context/current-context.service';
import { resolvePlacement } from './navigation-placement';
import { SUPPLEMENTAL_NAVIGATION_ITEMS } from './navigation-supplemental-items';
import {
  NAVIGATION_CAPABILITIES,
  NAVIGATION_MODULES,
  NAVIGATION_SUITES
} from './navigation-taxonomy';
import { isLegacyExperimentalNavItem } from './navigation-legacy-retirement';
import { DemoModeService } from './demo-mode.service';
import { isUnbuiltSuiteNavItem } from './navigation-unbuilt-suites';
import { REGISTERED_APP_ROUTES, RouteRegistryEntry } from './navigation-route-registry';

export interface NavigationTreeLeaf {
  kind: 'leaf';
  key: string;
  labelKey: string;
  route: string;
  status: NavigationItemStatus;
  sortOrder: number;
}

export interface NavigationTreeCapability {
  kind: 'capability';
  key: string;
  labelKey: string;
  status: NavigationItemStatus;
  sortOrder: number;
  children: NavigationTreeLeaf[];
}

export interface NavigationTreeModule {
  kind: 'module';
  key: string;
  labelKey: string;
  status: NavigationItemStatus;
  sortOrder: number;
  capabilities: NavigationTreeCapability[];
  /** Direct leaves when module has no capability grouping */
  directLeaves: NavigationTreeLeaf[];
}

export interface NavigationTreeSuite {
  kind: 'suite';
  key: string;
  labelKey: string;
  sortOrder: number;
  modules: NavigationTreeModule[];
}

export interface NavigationMismatchReport {
  routesWithoutMenu: RouteRegistryEntry[];
  menuRoutesWithoutRegistry: string[];
  duplicateMenuKeys: string[];
}

@Injectable({ providedIn: 'root' })
export class NavigationTreeService {
  private readonly contextService = inject(CurrentContextService);
  private readonly demoMode = inject(DemoModeService);
  private readonly mismatchReportState = signal<NavigationMismatchReport | null>(null);

  readonly mismatchReport = this.mismatchReportState.asReadonly();

  readonly mergedFlatItems = computed(() => this.mergeNavigationItems());

  readonly navigationTree = computed(() => this.buildTree(this.mergedFlatItems()));

  readonly visibleFlatItems = computed(() => {
    const roles = new Set(this.contextService.context()?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return this.mergedFlatItems();
    }

    const entitlements = this.contextService.context()?.entitlements ?? [];
    const permissionNodes = this.contextService.context()?.permissions ?? [];
    const permissionKeys = permissionNodes.map(item => item.key);
    return this.mergedFlatItems()
      .filter(item => hasEntitlement(item.requiredEntitlement, entitlements, permissionKeys))
      .filter(item => !item.requiredPermission || !!findPermissionNode(permissionNodes, item.requiredPermission).node);
  });

  readonly visibleNavigationTree = computed(() => this.buildTree(this.visibleFlatItems()));

  refreshMismatchReport(): NavigationMismatchReport {
    const items = this.mergedFlatItems();
    const menuRoutes = new Set(items.map(i => normalizeRoute(i.route)));
    const registryRoutes = REGISTERED_APP_ROUTES.filter(r => !r.isParameterized);

    const routesWithoutMenu = registryRoutes.filter(
      entry => !entry.excludeFromMenuCheck && !menuRoutes.has(normalizeRoute(entry.route))
    );

    const registryRouteSet = new Set(registryRoutes.map(r => normalizeRoute(r.route)));
    const menuRoutesWithoutRegistry = [...menuRoutes].filter(route => !registryRouteSet.has(route) && route !== '/dashboard');

    const keys = items.map(i => i.key);
    const duplicateMenuKeys = keys.filter((key, index) => keys.indexOf(key) !== index);

    const report: NavigationMismatchReport = {
      routesWithoutMenu,
      menuRoutesWithoutRegistry,
      duplicateMenuKeys: [...new Set(duplicateMenuKeys)]
    };
    this.mismatchReportState.set(report);
    if (routesWithoutMenu.length > 0 || menuRoutesWithoutRegistry.length > 0) {
      console.info('[NavigationTreeService] Route/menu mismatch report', report);
    }
    return report;
  }

  private mergeNavigationItems(): NavigationItem[] {
    const apiItems = this.contextService.navigation();
    const byKey = new Map<string, NavigationItem>();

    for (const item of apiItems) {
      byKey.set(item.key, enrichItem(item));
    }

    for (const supplemental of SUPPLEMENTAL_NAVIGATION_ITEMS) {
      byKey.set(supplemental.key, supplemental);
    }

    const showHiddenNav = this.demoMode.enabled();
    return [...byKey.values()]
      .filter(item => !isLegacyExperimentalNavItem(item))
      .filter(item => showHiddenNav || !isUnbuiltSuiteNavItem(item))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  private buildTree(items: NavigationItem[]): NavigationTreeSuite[] {
    type CapabilityBucket = { defKey: string; labelKey: string; sortOrder: number; status: NavigationItemStatus; leaves: NavigationTreeLeaf[] };
    type ModuleBucket = {
      defKey: string;
      labelKey: string;
      sortOrder: number;
      status: NavigationItemStatus;
      capabilities: Map<string, CapabilityBucket>;
      directLeaves: NavigationTreeLeaf[];
    };
    type SuiteBucket = { defKey: string; labelKey: string; sortOrder: number; modules: Map<string, ModuleBucket> };

    const suites = new Map<string, SuiteBucket>();

    for (const item of items) {
      const placement = resolvePlacement(item);
      const suiteDef = NAVIGATION_SUITES.find(s => s.key === placement.suiteKey);
      const moduleDef = NAVIGATION_MODULES.find(m => m.key === placement.moduleKey);
      if (!suiteDef || !moduleDef) {
        continue;
      }

      if (!suites.has(suiteDef.key)) {
        suites.set(suiteDef.key, {
          defKey: suiteDef.key,
          labelKey: suiteDef.labelKey,
          sortOrder: suiteDef.sortOrder,
          modules: new Map()
        });
      }

      const suite = suites.get(suiteDef.key)!;
      if (!suite.modules.has(moduleDef.key)) {
        suite.modules.set(moduleDef.key, {
          defKey: moduleDef.key,
          labelKey: moduleDef.labelKey,
          sortOrder: moduleDef.sortOrder,
          status: moduleDef.status ?? 'active',
          capabilities: new Map(),
          directLeaves: []
        });
      }

      const module = suite.modules.get(moduleDef.key)!;
      const leaf: NavigationTreeLeaf = {
        kind: 'leaf',
        key: item.key,
        labelKey: item.label,
        route: item.route,
        status: item.status ?? 'active',
        sortOrder: item.sortOrder
      };

      if (placement.capabilityKey) {
        const capDef = NAVIGATION_CAPABILITIES.find(c => c.key === placement.capabilityKey);
        const capKey = capDef?.key ?? placement.capabilityKey;
        if (!module.capabilities.has(capKey)) {
          module.capabilities.set(capKey, {
            defKey: capKey,
            labelKey: capDef?.labelKey ?? capKey,
            sortOrder: capDef?.sortOrder ?? 999,
            status: capDef?.status ?? 'active',
            leaves: []
          });
        }
        module.capabilities.get(capKey)!.leaves.push(leaf);
      } else {
        module.directLeaves.push(leaf);
      }
    }

    return [...suites.values()]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(suite => ({
        kind: 'suite' as const,
        key: suite.defKey,
        labelKey: suite.labelKey,
        sortOrder: suite.sortOrder,
        modules: [...suite.modules.values()]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(module => ({
            kind: 'module' as const,
            key: module.defKey,
            labelKey: module.labelKey,
            status: module.status,
            sortOrder: module.sortOrder,
            capabilities: [...module.capabilities.values()]
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(cap => ({
                kind: 'capability' as const,
                key: cap.defKey,
                labelKey: cap.labelKey,
                status: cap.status,
                sortOrder: cap.sortOrder,
                children: cap.leaves.sort((a, b) => a.sortOrder - b.sortOrder)
              }))
              .filter(cap => cap.children.length > 0),
            directLeaves: module.directLeaves.sort((a, b) => a.sortOrder - b.sortOrder)
          }))
          .filter(module => module.capabilities.length > 0 || module.directLeaves.length > 0)
      }))
      .filter(suite => suite.modules.length > 0);
  }
}

function enrichItem(item: NavigationItem): NavigationItem {
  const placement = resolvePlacement(item);
  return {
    ...item,
    suiteKey: item.suiteKey ?? placement.suiteKey,
    moduleKeyCanonical: item.moduleKeyCanonical ?? placement.moduleKey,
    capabilityKey: item.capabilityKey ?? placement.capabilityKey ?? null,
    level: item.level ?? 4,
    status: item.status ?? 'active'
  };
}

function normalizeRoute(route: string): string {
  const path = route.split(/[?#]/)[0] ?? route;
  return path.startsWith('/') ? path : `/${path}`;
}
